/*
  ShotTracker — GPS-based per-shot logger (v1).
  ------------------------------------------------------------------
  Renders as a compact pill inside the round's scoring card:

    [ + Shot (club) ]  Driver → 7i → PW    [undo]

  Tapping "+ Shot" opens a bottom-sheet club picker. When a club is
  chosen we grab the freshest GPS fix (via userPos prop, populated by
  the parent's existing geolocation watcher) and POST it to
  /api/rounds/{id}/shots. The backend computes distance from the
  previous shot on the same hole (or the tee) and returns the updated
  round; the parent re-renders with the new shots.

  Undo removes the most recent shot on the current hole for the
  current player, calling DELETE /api/rounds/{id}/shots/last.

  Kept intentionally standalone so Round.jsx doesn't grow — the only
  Round.jsx change is a single import + <ShotTracker … /> render.
*/
import { useEffect, useRef, useState } from "react";
import { CaretDown, ArrowCounterClockwise, MapPin, X, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";

// Kept in sync with backend _CLUB_LABELS. Order below drives the picker
// grid — most common clubs first, then irons in ascending loft.
const CLUB_ROWS = [
  [
    { key: "driver", label: "Driver", short: "D" },
    { key: "3w", label: "3-wood", short: "3W" },
    { key: "5w", label: "5-wood", short: "5W" },
    { key: "hybrid", label: "Hybrid", short: "Hy" },
  ],
  [
    { key: "3i", label: "3-iron", short: "3i" },
    { key: "4i", label: "4-iron", short: "4i" },
    { key: "5i", label: "5-iron", short: "5i" },
    { key: "6i", label: "6-iron", short: "6i" },
  ],
  [
    { key: "7i", label: "7-iron", short: "7i" },
    { key: "8i", label: "8-iron", short: "8i" },
    { key: "9i", label: "9-iron", short: "9i" },
    { key: "pw", label: "PW", short: "PW" },
  ],
  [
    { key: "gw", label: "GW", short: "GW" },
    { key: "sw", label: "SW", short: "SW" },
    { key: "lw", label: "LW", short: "LW" },
    { key: "putter", label: "Putter", short: "Pu" },
  ],
];

// ────────────────────────────────────────────────────────────────
// Auto-detect helpers
// ────────────────────────────────────────────────────────────────
// Standard great-circle → yards. Kept client-side so the auto-detector
// doesn't need a round-trip to know whether the player has moved enough
// to count as a shot.
const YDS_PER_METRE = 1.0936133;
const _haversineYards = (a, b) => {
  const R = 6371000; // metres
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c * YDS_PER_METRE;
};

// Guess the club based on how far the ball actually travelled. Deliberately
// coarse — the user can edit any auto-logged shot on the round summary and
// once real Bag IQ averages exist, we'll swap this table for per-user data.
const clubFromDistance = (yds) => {
  if (yds >= 230) return "driver";
  if (yds >= 200) return "3w";
  if (yds >= 175) return "5w";
  if (yds >= 155) return "hybrid";
  if (yds >= 140) return "5i";
  if (yds >= 125) return "7i";
  if (yds >= 110) return "8i";
  if (yds >= 95) return "9i";
  if (yds >= 75) return "pw";
  if (yds >= 55) return "gw";
  if (yds >= 25) return "sw";
  return "putter";
};

// Constants tuned so a normal walk-to-your-ball → address → hit → walk-to-
// next-ball cycle fires exactly one auto-detected shot. Golfers casually
// milling around between shots stay within STATIONARY_RADIUS_YDS.
const STATIONARY_RADIUS_YDS = 6;
const STATIONARY_DURATION_MS = 8000;
const MIN_AUTO_SHOT_YDS = 25;

const shortLabelForClub = (key) => {
  for (const row of CLUB_ROWS) {
    const f = row.find((c) => c.key === key);
    if (f) return f.short;
  }
  return key;
};

export default function ShotTracker({
  roundId,
  holeNumber,
  playerIndex = 0,
  userPos,
  shots = [],
  onShotsChanged,
}) {
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Auto-detect toggle. Persisted so mates set it once at the first tee
  // and it sticks for the whole round + future rounds.
  const [autoDetect, setAutoDetect] = useState(() => {
    try { return localStorage.getItem("fw_auto_shot_detect") === "1"; }
    catch { return false; }
  });
  const [autoBanner, setAutoBanner] = useState(null); // {club, yds} last auto-shot for a subtle in-round toast

  const toggleAutoDetect = () => {
    setAutoDetect((v) => {
      const next = !v;
      try { localStorage.setItem("fw_auto_shot_detect", next ? "1" : "0"); } catch { /* private mode */ }
      return next;
    });
  };

  // Only the shots for THIS hole + THIS player (so fourball shows the
  // active player's shots, not everyone's).
  const holeShots = shots
    .filter((s) => s.hole_number === holeNumber && (s.player_index || 0) === (playerIndex || 0))
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  const logShot = async (clubKey, { silent = false } = {}) => {
    if (!silent) setError("");
    if (!userPos || typeof userPos.lat !== "number" || typeof userPos.lng !== "number") {
      if (!silent) setError("Turn on GPS first — we need your position to log the shot.");
      return null;
    }
    if (!silent) setSaving(true);
    try {
      const { data } = await api.post(`/rounds/${roundId}/shots`, {
        hole_number: holeNumber,
        club: clubKey,
        lat: userPos.lat,
        lng: userPos.lng,
        player_index: playerIndex,
      });
      setPicker(false);
      if (onShotsChanged) onShotsChanged(data.shots || []);
      return data;
    } catch (e) {
      if (!silent) setError(e?.response?.data?.detail || "Could not log shot");
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const undoLast = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await api.delete(
        `/rounds/${roundId}/shots/last?hole_number=${holeNumber}&player_index=${playerIndex}`
      );
      if (onShotsChanged) onShotsChanged(data.shots || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not undo shot");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Auto-detect state machine
  // ─────────────────────────────────────────────────────────────
  // We track:
  //   • anchor       — the last confirmed stationary position (where the
  //                    previous shot was played from). Updated when a shot
  //                    is logged (manual or auto), when the hole changes,
  //                    or when we first arrive on this hole.
  //   • pendingSpot  — a candidate new stationary position we're waiting
  //                    to see stable (STATIONARY_RADIUS_YDS for
  //                    STATIONARY_DURATION_MS). Once confirmed and it's
  //                    far enough from the anchor, we auto-log.
  // Both live in refs (not state) because the detection loop should never
  // trigger a re-render — only the shot-log network call does.
  const anchorRef = useRef(null);
  const pendingRef = useRef(null); // { lat, lng, since }

  // Whenever the player moves to a new hole, wipe the state so shots from
  // hole N-1 don't leak into hole N.
  useEffect(() => {
    anchorRef.current = null;
    pendingRef.current = null;
  }, [holeNumber, playerIndex]);

  // Seed the anchor from the most recent logged shot on this hole (so if
  // the user logged one manually and then enables auto-detect, the next
  // auto shot is measured from their last known landing spot, not the tee).
  useEffect(() => {
    const last = holeShots[holeShots.length - 1];
    if (last) anchorRef.current = { lat: last.lat, lng: last.lng };
  }, [holeShots.length]); // length change = a shot was added

  // Position-arrival hook — runs every time the parent's userPos updates.
  // Kept small: figure out whether we've "stopped", and if so, whether the
  // stop is a new anchor (auto-log a shot) or the same anchor (no-op).
  useEffect(() => {
    if (!autoDetect) return;
    if (!userPos || typeof userPos.lat !== "number" || typeof userPos.lng !== "number") return;
    // Ignore noisy GPS pings — accuracy worse than 25 m is not useful for
    // detecting a 25-yard shot.
    if (typeof userPos.accuracy === "number" && userPos.accuracy > 25) return;

    // First fix on this hole → seed the anchor and wait.
    if (!anchorRef.current) {
      anchorRef.current = { lat: userPos.lat, lng: userPos.lng };
      return;
    }

    const now = Date.now();

    // Are we still close to the current pending spot? Extend the timer.
    // Otherwise start a fresh candidate.
    if (
      pendingRef.current
      && _haversineYards(pendingRef.current, userPos) <= STATIONARY_RADIUS_YDS
    ) {
      const stableFor = now - pendingRef.current.since;
      if (stableFor >= STATIONARY_DURATION_MS) {
        const yds = _haversineYards(anchorRef.current, userPos);
        if (yds >= MIN_AUTO_SHOT_YDS) {
          // Fire and forget — logShot updates shots via onShotsChanged.
          const club = clubFromDistance(yds);
          logShot(club, { silent: true }).then((data) => {
            if (data) {
              setAutoBanner({ club, yds: Math.round(yds) });
              setTimeout(() => setAutoBanner(null), 3200);
            }
          });
          // Advance the anchor to the new stable spot regardless of network
          // outcome — even if the POST failed, we don't want to keep
          // re-triggering on the same movement.
          anchorRef.current = { lat: userPos.lat, lng: userPos.lng };
          pendingRef.current = null;
        }
      }
    } else {
      pendingRef.current = { lat: userPos.lat, lng: userPos.lng, since: now };
    }
  }, [userPos?.lat, userPos?.lng, autoDetect, holeNumber]);

  return (
    <div className="mt-3 pt-3 border-t border-white/15" data-testid="shot-tracker">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setPicker(true)}
          disabled={saving}
          data-testid="shot-tracker-open"
          className="flex-1 flex items-center justify-center gap-2 rounded-full bg-white/12 hover:bg-white/20 disabled:opacity-50 py-2 text-sm font-bold transition-colors"
        >
          <MapPin size={14} weight="fill" />
          <span>+ Log shot</span>
          <CaretDown size={12} weight="bold" className="opacity-70" />
        </button>
        <button
          type="button"
          onClick={toggleAutoDetect}
          data-testid="shot-tracker-auto-toggle"
          className={`rounded-full py-2 px-3 text-[11px] font-bold flex items-center gap-1 transition-colors ${
            autoDetect
              ? "bg-[var(--accent-terracotta,#C4543C)] text-white"
              : "bg-white/8 hover:bg-white/15"
          }`}
          title={autoDetect ? "Auto-detect ON — walking away from your ball logs a shot" : "Auto-detect OFF — tap to enable"}
          aria-pressed={autoDetect}
        >
          <Sparkle size={12} weight="fill" />
          <span>Auto</span>
        </button>
        {holeShots.length > 0 && (
          <button
            type="button"
            onClick={undoLast}
            disabled={saving}
            data-testid="shot-tracker-undo"
            className="rounded-full bg-white/8 hover:bg-white/15 disabled:opacity-50 p-2 transition-colors"
            title="Undo last shot"
            aria-label="Undo last shot"
          >
            <ArrowCounterClockwise size={16} weight="bold" />
          </button>
        )}
      </div>

      {autoDetect && (
        <p
          className="text-center text-[10px] font-bold mt-1 opacity-70"
          data-testid="shot-tracker-auto-hint"
        >
          Auto-detect on · walk to your ball, we log the shot
        </p>
      )}

      {autoBanner && (
        <div
          className="mt-2 rounded-lg px-2.5 py-1.5 text-center text-[11px] font-bold flex items-center justify-center gap-1.5"
          style={{ background: "rgba(196,84,60,.18)", color: "var(--accent-terracotta,#C4543C)" }}
          data-testid="shot-tracker-auto-banner"
        >
          <Sparkle size={12} weight="fill" />
          <span>Auto-logged · {shortLabelForClub(autoBanner.club)} · {autoBanner.yds}y</span>
        </div>
      )}

      {/* Shots taken on this hole */}
      {holeShots.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 justify-center" data-testid="shot-tracker-list">
          {holeShots.map((s, i) => (
            <div
              key={s.id}
              className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono-num font-bold"
              style={{ background: "rgba(255,255,255,.12)" }}
              data-testid={`shot-chip-${i}`}
            >
              <span className="opacity-70">{i + 1}.</span>
              <span>{shortLabelForClub(s.club)}</span>
              {typeof s.distance_y === "number" && (
                <span className="opacity-70">· {Math.round(s.distance_y)}y</span>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-center text-[11px] text-[var(--accent-terracotta,#C4543C)] mt-2" data-testid="shot-tracker-error">
          {error}
        </p>
      )}

      {/* Bottom-sheet club picker */}
      {picker && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/55 p-4"
          onClick={() => !saving && setPicker(false)}
          data-testid="shot-tracker-picker"
        >
          <div
            className="w-full max-w-md rounded-3xl p-5"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--bg-card, #fff)", color: "var(--primary, #143D2F)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-60">Hole {holeNumber}</p>
                <h3 className="font-heading font-black text-xl leading-tight">Pick a club</h3>
              </div>
              <button
                type="button"
                onClick={() => setPicker(false)}
                data-testid="shot-tracker-picker-close"
                className="rounded-full p-2 hover:bg-black/5"
                aria-label="Close club picker"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="text-xs opacity-70 mb-3">
              We&apos;ll grab your GPS position and log the shot. Yards travelled shows up on your shot map after the round.
            </p>
            <div className="space-y-2">
              {CLUB_ROWS.map((row, ri) => (
                <div key={ri} className="grid grid-cols-4 gap-2">
                  {row.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => logShot(c.key)}
                      disabled={saving}
                      data-testid={`shot-club-${c.key}`}
                      className="rounded-2xl py-2 text-center font-bold text-sm disabled:opacity-50 active:scale-95 transition-transform"
                      style={{
                        background: "var(--accent-sand, #EADEC8)",
                        color: "var(--primary, #143D2F)",
                      }}
                    >
                      <div className="text-[10px] font-mono-num font-black">{c.short}</div>
                      <div className="text-[9px] font-normal opacity-70 leading-tight mt-0.5">{c.label}</div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {saving && (
              <p className="text-center text-xs mt-3 opacity-70">Logging shot…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
